import React, { FC } from 'react';

import { SwiperOptions } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, mediumDesktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useParams, usePathname } from 'next/navigation';
import { Configure, useHits } from 'react-instantsearch-hooks-web';
import ProductTile from '../product-tile';
import { mapProduct } from 'helpers/algolia/map-product';
import { breakpoints } from 'helpers/utils/breakpoints';
import { localeMapping } from 'helpers/constants/localeMapping';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface ProductSliderYoulikeProps extends Partial<SwiperOptions> {
   data:any
}

const ProductSliderAlgoliaYouLike: FC<ProductSliderYoulikeProps> = ({
  data
}) => {
  const { isTouchDevice } = useTouchDevice();

  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isMobileSize] = useMediaQuery(mobile);
  const [isDesktopSize] = useMediaQuery(desktop);
 
  const currentURL = usePathname();
  const { locale, slug } = useParams();
  const { hits } = useHits();
  const localeKey = localeMapping[locale];

  const isProductDetailPage = isPdpUrl(currentURL);
  const categorySlug = (slug as string).split('/').at(-1);

  const getFilterQuery = () => {
    if (data?.dataSource?.categories?.data) {
        const categoryName: string[] = [];
        data.dataSource.categories.data.forEach((category: { name: { [x: string]: string; }; }) => {
            if (category.name && category.name[`${localeKey}`]) {
                categoryName.push(`categoryKeys.${localeKey}:"${category.name[`${localeKey}`]}"`);
            }
        });
        return categoryName.join(' OR ');
    }
    return ''; 
};

  return (
    <div
      id="related-products"
      className={`py-32 pl-24 md:pb-92 md:pt-48 lg:py-48 lg:px-48 xl:px-60 2xl:px-80 xl:py-100  ${
        isProductDetailPage
          ? 'relative left-full w-screen -translate-x-full bg-[#F3EDE9] px-48 md:pb-1 md:pt-100 lg:grid lg:grid-cols-12 lg:pl-0 xl:pb-153 xl:pt-153'
          : 'bg-[#E5E9EB] md:px-48'
      }`}
    >
      <Configure query={''} filters={getFilterQuery()} hitsPerPage={12} facets={['*']} maxValuesPerFacet={999} 
        attributesToHighlight={[]} // Disable highlighting
        attributesToSnippet={[]} // Disable snippetting
        attributesToRetrieve={[
          "name",
          "slug",
          "categoryKeys",
          "masterVariantID",
          "searchableAttributes.IsNew",
          "variants.id",
          "variants.key",
          "variants.sku",
          "variants.images",
          "variants.attributes.colour",
          "variants.attributes.item-code",
          "variants.attributes.item-name",
        ]} 
      />
      <div className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4' : ''}>
        <div>
          <Typography
            id="related-products-title"
            className={`pb-32 text-32 font-medium text-primary-black xl:text-36 2xl:text-48  ${
              isProductDetailPage ? '' : ''
            } `}
            as="h3"
          >
            You might also like
          </Typography>
        </div>
        <div className={isProductDetailPage ? '' : ''}>
          <div className="product-slider-container relative w-full">
            <Slider
              className="mb-48"
              slidesPerView={2.1}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              arrows={isTabletSize || isDesktopSize || isMiniTabletSize}
              innerArrows
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '110%', left: 50, padding: '10px' }}
              prevButtonStyles={{ top: '110%', left: 0, padding: '10px' }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{
                [tablet]: {
                  slidesPerView: isProductDetailPage ? 3.25 : 4,
                  slidesPerGroup: 4,
                  spaceBetween: 16,
                  ...(breakpoints[mediumDesktop] ?? {}),
                },
                [smallMobile]: {
                  slidesPerView: isProductDetailPage ? 1.2 : 1.2,
                  slidesPerGroup: 1,
                  spaceBetween: 16,
                  ...(breakpoints[smallMobile] ?? {}),
                },
                [mobile]: {
                  slidesPerView: isProductDetailPage ? 2.1 : 2.1,
                  slidesPerGroup: 2,
                  spaceBetween: 16,
                  ...(breakpoints[mobile] ?? {}),
                },
              }}
              >
              {hits.map((hit: any, index:number) => (
                <ProductTile
                  key={index}
                  images={hit?.images}
                  product={mapProduct(hit, locale)}
                />
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSliderAlgoliaYouLike;

